import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import AdapterDateFns from '@date-io/date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Alert,
  Button,
  DialogActions,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Theme
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { createStyles, makeStyles } from '@mui/styles';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { setDocumentDetail } from 'src/store/documentDetail';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { AppState, store, useAppSelector } from '../../../store';
import { setSnackbarMessage } from '../../../store/snackbarMessage';
import DocManagementService from '../documentUpload/service/DocManagementService';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    topScrollPaper: {
      alignItems: 'flex-start'
    },
    topPaperScrollBody: {
      verticalAlign: 'top'
    }
  })
);

const SendForReviewDialogComponent = (props: any) => {
  const { openSendForReviewDialog, setOpenSendForReviewDialog, qualityControlId, documentDetail, actionName } = props;
  const { userPreferedDateFormat, preferedFormatForDatePicker } = usePreferedDateFormat();
  const actionText = actionName === 'Send For Review' ? 'review' : 'approval';
  const [errorUploadedDueData, setErrorUploadedDueData] = useState<string>('');
  const classes = useStyles();
  const [value, setValue] = React.useState<string | Date | null>(null);
  const [priority, setPriority] = React.useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [openConfirmExit, setOpenConfirmExit] = useState(false);
  const [init, setInit] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [userError, setUsersError] = useState(null);
  const [isProcessing, setProcessing] = useState(false);
  const [approveUser, setApproveUser] = useState();
  const docService = new DocManagementService();
  const dispatch = useDispatch();
  const user: any = useAppSelector(state => state.user.user);
  const userName = `${user.firstName + ' ' + user.lastName}`;
  const isfLevel = documentDetail?.data[0]?.isf_doc_level;
  const selectedStudy = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Study);
  const selectedCountry = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Country);
  const selectedSite = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Site);
  const entity = documentDetail?.data[0]?.isf_document_name;

  useEffect(() => {
    setInit(false);
    getAllUsers();
  }, []);

  const handleInvalidDueDate = (err: any, value: any) => {
    if (err === null) setErrorUploadedDueData('');
    if (err === 'minDate') {
      setErrorUploadedDueData('Due data can not be past date');
    } else if (err === 'disablePast') {
      setErrorUploadedDueData('Past dates are not allowed');
    } else if (err === 'invalidDate') {
      setErrorUploadedDueData('Invalid date format');
    }
  };

  const getAllUsers = async () => {
    setProcessing(true);
    const response: any = await docService.getAllUsers(
      selectedStudy,
      isfLevel,
      documentDetail.data[0].isf_artifact_name,
      selectedCountry,
      selectedSite
    );

    setInit(true);
    if (response?.data) {
      const userData = response?.data.filter((u: any) => u.email !== null && u.email !== user.email);
      setUsersList(userData);
      setUsersError(null);
    } else {
      setUsersError(response?.error);
    }
    setProcessing(false);
  };
  const handleClose = () => {
    if (approveUser || selectedUsers?.length || priority || value) {
      setOpenConfirmExit(true);
    } else {
      setOpenSendForReviewDialog(false);
      setOpenConfirmExit(false);
    }
  };

  const handleChangeMultiple = (event: any, values: any) => {
    setApproveUser(values[0]);
    setSelectedUsers(values);
  };

  const handleChangeSingle = (event: any, values: any) => {
    setApproveUser(values);
    const singleUser: any = [];
    singleUser.push(values);
    setSelectedUsers(singleUser);
  };

  const handleChange = (event: SelectChangeEvent) => {
    setPriority(event.target.value as string);
  };

  const createWorkFLowTask = async (docData: any) => {
    setProcessing(true);
    const res: any = await docService.createTask(
      documentDetail.data[0]?.isf_document_id,
      docData,
      true,
      documentDetail.data[0]?.isf_doc_study,
      documentDetail.data[0]?.isf_doc_country,
      documentDetail.data[0]?.isf_doc_site,
      documentDetail.data[0]?.isf_doc_level,
      process.env.REACT_APP_NAME,
      actionName === 'Ready For Approval' ? 'DocumentApproval' : 'DocumentReview',
      entity
    );
    setProcessing(false);
    setOpenSendForReviewDialog(false);
    setOpenConfirmExit(false);
    if (res.data) {
      if (actionName === 'Ready For Approval') {
        const data = {
          is_resolved: false,
          action_name: 'Ready For Approval',
          updated_by: userName
        };
        const response: any = await docService.updateStatus(
          res.data?.id,
          data,
          documentDetail.data[0]?.isf_doc_study,
          documentDetail.data[0]?.isf_doc_country,
          documentDetail.data[0]?.isf_doc_site,
          documentDetail.data[0]?.isf_doc_level,
          process.env.REACT_APP_NAME,
          actionName === 'Ready For Approval' ? 'DocumentApproval' : 'DocumentReview',
          entity
        );
        const query = {
          query_filters: [
            {
              name: 'isf_document_id',
              value: documentDetail?.data[0]?.isf_document_id,
              operation: 'equals'
            }
          ],
          filter_types: ['All']
        };
        const result: any = await docService.getSearchResult(
          query,
          selectedStudy,
          selectedCountry,
          selectedSite,
          isfLevel,
          selectedStudy === '--' ? false : true,
          process.env.REACT_APP_NAME,
          actionName === 'Ready For Approval' ? 'DocumentApproval' : 'DocumentReview',
          entity
        );
        setDocumentDetail(dispatch, [
          {
            ...documentDetail.data[0],
            isBtnDisabled: true,
            isf_doc_status: 'Ready for approval',
            isf_doc_viewer_approval_cycle: result?.data?.selected_data[0]?.document_data.isf_doc_viewer_approval_cycle,
            isf_doc_approval_date: result?.data?.selected_data[0]?.document_data.isf_doc_approval_date,
            isf_doc_approval_st_dt: result?.data?.selected_data[0]?.document_data.isf_doc_approval_st_dt
          }
        ]);
      } else {
        setDocumentDetail(dispatch, [
          {
            ...documentDetail.data[0],
            isBtnDisabled: true
          }
        ]);
      }
      store.dispatch(setSnackbarMessage(`Document is sent for ${actionText} successfully.`, 'success'));
    } else {
      store.dispatch(setSnackbarMessage(`${actionText} Workflow should be initiated.`, 'error'));
    }
  };

  const handleWorkFlow = () => {
    if (handleInvalidDate()) return;
    setUsersError(null);
    const userIds: any = [];
    selectedUsers.forEach((item: any) => {
      for (const [key] of Object.entries(item)) {
        if (key === 'id') {
          userIds.push({
            user_id: item.id,
            user_name: item.firstName + ' ' + item.lastName,
            user_email_id: item.email
          });
        }
      }
    });
    const docData: any = {
      users: userIds,
      isf_doc_status: documentDetail.data[0]?.isf_doc_status,
      task_id: 'test',
      task_title: 'test',
      action_name: actionName,
      due_date: value !== null ? `${moment(value).format('YYYY-MM-DD')}T00:00:00` : '', // ensuring consistency in date representations across different time zones.
      priority: priority,
      created_by: userName,
      created_by_email: user.email
    };
    createWorkFLowTask(docData);
  };

  const handleInvalidDate = () => {
    if (errorUploadedDueData !== '') {
      store.dispatch(setSnackbarMessage(errorUploadedDueData, 'error'));
      return true;
    }
  };

  return (
    <>
      <PopupComponent
        open={openSendForReviewDialog}
        scroll="paper"
        classes={{
          scrollPaper: classes.topScrollPaper,
          paperScrollBody: classes.topPaperScrollBody
        }}
        className="senddata-modal"
      >
        {actionName === 'Send For Review' ? (
          <DialogTitle>Send</DialogTitle>
        ) : (
          <DialogTitle>Ready For Approval</DialogTitle>
        )}

        {isProcessing && <LinearProgress />}
        <DialogContent className="senddata-modalcontent-new">
          <DialogContentText sx={{ minWidth: '60%' }}>
            {actionName === 'Send For Review' ? (
              <h4>Select user(s) to {actionText}:</h4>
            ) : (
              <h4>Select user to approve:</h4>
            )}
          </DialogContentText>
          {usersList?.length > 0 && (
            <Stack spacing={3} sx={{ minWidth: '60%' }}>
              {actionName === 'Send For Review' ? (
                <Autocomplete
                  disabled={isProcessing}
                  multiple
                  id="tags-standard-users"
                  options={usersList}
                  getOptionLabel={(option: any) =>
                    option.firstName + ' ' + option.lastName + ' (' + option?.email + ')'
                  }
                  onChange={handleChangeMultiple}
                  renderInput={params => <TextField {...params} variant="standard" label="Users" placeholder="Users" />}
                />
              ) : (
                <Autocomplete
                  disabled={isProcessing}
                  id="tags-standard-users"
                  options={usersList}
                  getOptionLabel={(option: any) =>
                    option.firstName + ' ' + option.lastName + ' (' + option?.email + ')'
                  }
                  onChange={handleChangeSingle}
                  renderInput={params => <TextField {...params} variant="standard" label="User" placeholder="Users" />}
                />
              )}
            </Stack>
          )}
          <FormControl fullWidth sx={{ mt: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} variant="standard" className="custom-datePicker">
              <DesktopDatePicker
                disablePast
                inputFormat={userPreferedDateFormat}
                label={
                  <span>
                    Due Date <span style={{ color: 'red' }}>*</span>
                  </span>
                }
                value={preferedFormatForDatePicker(value)}
                onError={(err: any, value: any) => handleInvalidDueDate(err, value)}
                onChange={(newValue: any) => {
                  if (newValue !== null) {
                    setValue(newValue); // Store the Date object directly for fix - 8520
                  }
                }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    onBlur={() => {
                      setValue(prevValue => {
                        const formattedValue = preferedFormatForDatePicker(prevValue);
                        return formattedValue;
                      });
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 3 }} variant="standard">
            <InputLabel id="priority-select-label">
              Priority
              <span style={{ color: 'red', fontSize: '12px' }}>*</span>
            </InputLabel>
            <Select
              labelId="priority-select-label"
              id="priority-select"
              value={priority}
              label="Priority"
              onChange={handleChange}
            >
              <MenuItem value={'Low'}>Low</MenuItem>
              <MenuItem value={'Medium'}>Medium</MenuItem>
              <MenuItem value={'High'}>High</MenuItem>
            </Select>
          </FormControl>
          {!init && (
            <Stack spacing={3} sx={{ minWidth: '60%' }}>
              <div>Loading ...</div>
            </Stack>
          )}

          {init && !userError && !usersList?.length && (
            <Stack spacing={3} sx={{ minWidth: '60%' }}>
              <Alert severity="error">No eligible users to submit document for review.</Alert>
            </Stack>
          )}

          {userError && (
            <Stack spacing={3} sx={{ minWidth: '60%' }}>
              <Alert severity="error">{userError}</Alert>
              {/*<Button variant="text" className="grey-btn" onClick={getAllUsers}>Retry</Button>*/}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" className="grey-btn" onClick={handleClose} disabled={isProcessing}>
            Close
          </Button>
          <Button
            disabled={
              isProcessing || !approveUser || !selectedUsers?.length || !priority || !value || value === 'Invalid Date'
            }
            onClick={handleWorkFlow}
          >
            Send
          </Button>
        </DialogActions>
      </PopupComponent>
      <div>
        <PopupComponent
          open={openConfirmExit}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="confirm-modal"
          child={true}
        >
          <DialogTitle id="alert-dialog-title">
            <b>Please Confirm</b>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              All unsaved changes will be lost. Are you sure you want to exit before saving your changes?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenConfirmExit(false);
              }}
              className="grey-btn"
            >
              CLOSE
            </Button>
            <Button
              onClick={() => {
                setOpenSendForReviewDialog(false);
                setOpenConfirmExit(false);
              }}
            >
              EXIT
            </Button>
          </DialogActions>
        </PopupComponent>
      </div>
    </>
  );
};

export default React.memo(SendForReviewDialogComponent);
